import { SiteContent, ThemeSiteContentResponse } from './../../_models/theme-site-content/theme-site-content.model';
import { environment } from './../../../environments/environment';
import { Injectable } from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { catchError, map, tap } from 'rxjs/operators';
import { BaseService } from '../../_services/base/base.service';
import { Router } from '@angular/router';
import { SessionUserHashManagerService } from '../../_services/session-user-hash-manager/session-user-hash-manager.service';

@Injectable({
  providedIn: 'root'
})
export class ThemeService extends BaseService {
  // Unified LEAP brand (35749): one theme for every brand. Site-content labels and footer links
  // resolve from the TP content set (favour TP/Evolve); chrome is neutral (LEAP title, SEG favicon).
  // Brand is a data attribute now (badges/filters), never a theme.
  public static readonly UnifiedContentKey = 'TP';

  private activeThem = new BehaviorSubject(ThemeService.UnifiedContentKey);
  public currentTheme = ThemeService.UnifiedContentKey;
  private endpoint: string = environment.apiUrl;
  public siteContent = new SiteContent();

  constructor(
    private httpClient: HttpClient,
    protected hashManagerService: SessionUserHashManagerService
    ) {
    super(hashManagerService);
      this.endpoint += this.endpoint.endsWith("/") ? "" : "/";
    }

  public getActiveTheme() {
    return this.activeThem.asObservable();
  }

  public setActiveTheme(name: string, callApi: boolean = true) {
    // Unified brand: the requested name is ignored — every caller gets the one LEAP brand.
    // Deferred to a zone-patched microtask: several components call this synchronously during their
    // init (login pages, session-overview fullscreen, route-params subscriptions), i.e. while change
    // detection is already running. Mutating currentTheme mid-pass flips bindings that were already
    // checked (NG0100). NOTE: must be Promise.then, not queueMicrotask — zone.js does not patch
    // queueMicrotask, so the mutation would happen outside Angular's zone.
    Promise.resolve().then(() => this.applyActiveTheme(callApi));
  }

  private applyActiveTheme(callApi: boolean) {
    this.currentTheme = ThemeService.UnifiedContentKey;
    this.activeThem.next(ThemeService.UnifiedContentKey);

    if (callApi) {
      this.setThemeContentValues(ThemeService.UnifiedContentKey).subscribe((response: ThemeSiteContentResponse[]) => {
        response.forEach((item: ThemeSiteContentResponse) => {
          this.siteContent[this.toCamelCase(item.labelName)] = item.labelText;
        });
      });
    }

    var favIcon = document.querySelector("link[rel*='icon']");
    favIcon.setAttribute("href", "assets/icons/Orbit_Icon_Full_Colour.png");
    var title = document.querySelector("title");
    title.innerHTML = "Tuition Management";
    this.setGtmId(environment.leapGtmId);
  }

  private setGtmId(gtmId: string) {
    if (document.querySelector(`script[data-gtm-id="${gtmId}"]`)) return;    
  
    const script = document.createElement('script');
    script.setAttribute('data-gtm-id', gtmId);
    script.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});
        var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';
        j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
        f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${gtmId}');
    `;
    document.head.appendChild(script);    
  }

  private setThemeContentValues(companyName: string) {

    return this.httpClient.post(this.endpoint + 'api/config/ReadThemeSiteContent', 
    { "companyName": companyName },
    this.prepareOptions({ "companyName": companyName })).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('setThemeContentValues'))
    );
  }
  
  private toCamelCase(value: string): string {
    return value.charAt(0).toLowerCase() + value.slice(1);
  }

}
